import { useState } from "react";
import { X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiError } from "../lib/api";
import { packageByKey, fmtIso } from "../lib/packages";

const inputCls = "bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]";
const TABS = [["extend", "Extend Plan"], ["discount", "Discount Next Plan"]];

const History = ({ pkg, onRemove }) => {
  const rows = [
    ...pkg.extensions.map((e) => ({ kind: "extensions", id: e.id, text: `Extended by ${e.days} day${e.days === 1 ? "" : "s"}`, reason: e.reason })),
    ...pkg.discounts.map((d) => ({ kind: "discounts", id: d.id, text: `${inr(d.amount)} off the plan starting ${fmtIso(d.cycle_start)}`, reason: d.reason })),
  ];
  if (!rows.length) return null;
  return (
    <div className="space-y-2 mb-5" data-testid="pkg-change-history">
      {rows.map((r) => (
        <div key={r.id} className="flex items-center justify-between border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-2 text-xs">
          <span className="text-[#D4AF37]">{r.text}{r.reason ? <span className="text-white/50"> · {r.reason}</span> : null}</span>
          <button type="button" onClick={() => onRemove(r)} className="text-red-400 hover:text-red-300" data-testid={`pkg-change-remove-${r.id}`}><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
};

export const PackageChangeModal = ({ row, onClose, onSaved }) => {
  const pkg = row.package;
  const [tab, setTab] = useState("extend");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (tab === "extend") await api.post(`/admin/fee-plans/${pkg.plan_id}/extensions`, { days: Number(value), reason: reason || null });
      else await api.post(`/admin/fee-plans/${pkg.plan_id}/discounts`, { amount: Number(value), reason: reason || null });
      toast.success(tab === "extend" ? `Plan extended by ${value} days` : `${inr(Number(value))} discount added to the next plan`);
      onSaved();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    try { await api.delete(`/admin/fee-plans/${pkg.plan_id}/${r.kind}/${r.id}`); toast.success("Removed"); onSaved(); onClose(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="pkg-change-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-8 w-full max-w-md">
        <div className="flex justify-between items-start mb-2">
          <h2 className="font-heading text-xl font-black">Plan Extension / Discount</h2>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="pkg-change-close"><X size={20} /></button>
        </div>
        <p className="text-white/50 text-sm mb-5">{row.player.name} · {packageByKey(pkg.package)?.label}{pkg.cycle_end ? ` · current plan ends ${fmtIso(pkg.cycle_end)}` : ""} · next renewal {fmtIso(pkg.next_renewal)}</p>
        <History pkg={pkg} onRemove={remove} />
        <div className="flex border border-white/10 mb-4">
          {TABS.map(([k, l]) => (
            <button type="button" key={k} onClick={() => { setTab(k); setValue(""); }} className={`flex-1 text-[10px] uppercase tracking-widest py-2.5 transition-colors duration-200 ${tab === k ? "bg-[#D4AF37] text-black" : "text-white/60 hover:text-white"}`} data-testid={`pkg-change-tab-${k}`}>{l}</button>
          ))}
        </div>
        <p className="text-white/40 text-xs mb-3">{tab === "extend"
          ? "Pushes the current plan's end date and every later renewal and due date by this many days."
          : `One-time ₹ off the next plan only (fee ${inr(pkg.group_fee)}). The ongoing plan price is unchanged.`}</p>
        <input required type="number" min="1" placeholder={tab === "extend" ? "Number of days" : "Discount ₹"} value={value} onChange={(e) => setValue(e.target.value)} className={inputCls + " w-full mb-3"} data-testid="pkg-change-value" />
        <input placeholder="Reason (e.g. medical emergency)" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls + " w-full mb-4"} data-testid="pkg-change-reason" />
        <button type="submit" className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="pkg-change-save">
          {tab === "extend" ? "Extend Plan" : "Add Discount"}
        </button>
      </form>
    </div>
  );
};
